import React, { useState, useEffect, useRef } from 'react';
import { Bell, XCircle, AlertCircle, CreditCard, Download, Zap, Check, ExternalLink, Clock } from 'lucide-react';
import server from '../../../api/server.js';
import "../../../dashboard-components/loader.css";
import { useToast } from '../../../context/ToastContext.jsx';
import { FaTimes } from 'react-icons/fa';
import { scheduleAccessTokenRefresh } from '../../../utils/dashboardAuth';

const NotificationsPanel = ({serviceID, clientID}) => {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('unread');
  const [showOlderNotifications, setShowOlderNotifications] = useState(false);
  const [service_id, setService_id] = useState(null);
  const [client_id, setClient_id] = useState(null);
  const [email, setEmail] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const currentDate = new Date();
  const sevenDaysAgo = new Date(currentDate);
  sevenDaysAgo.setDate(currentDate.getDate() - 7);
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedTime, setLastFetchedTime] = useState(null);
  const intervalRef = useRef(null);
  const isFetchingRef = useRef(false);

  const unreadNotifications = notifications.filter(notif => !notif.is_read);
  const recentNotifications = notifications;
  
  const displayedNotifications = activeTab === 'unread' 
    ? unreadNotifications 
    : (showOlderNotifications ? notifications : recentNotifications);

  const getAllNotifications = async () => {
    try {
      let timenow = parseInt(Date.now() / 1000);
      let timelast7Days = timenow - 7 * 24 * 60 * 60;
      const resp = await server.getAllNotifications(client_id, service_id, email, 'home', undefined, timelast7Days, timenow);
      if(resp.status === 200){
        const data = await resp.json();
        setNotifications(data.notifications.sort((a,b)=> {return b.time-a.time}));
      }
    } catch (error) {
    }
  }
  const toggleNotificationPanel = () => {
    getAllNotifications();
    setIsOpen(!isOpen);
  };

  const markAsRead = async (id) => {
    try {
      setLoading(true);
      let resp = await server.markNotificationAsRead({client_id, service_id, email, menu: 'home',submenu: undefined, notification_ids: JSON.stringify([id])});
      if(resp.status === 200){
        getAllNotifications();
        getUnreadNotificationCount();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      let resp = await server.markNotificationAsRead({client_id, service_id, email, menu: 'home',submenu: undefined, notification_ids: JSON.stringify(notifications.map(notif => notif.id))});
      if(resp.status === 200){
        getAllNotifications();
        getUnreadNotificationCount();
      }
    } catch (error) {
    }
  };

  const handleShowOlderNotifications = async() => {
    try {
      setLoading(true);
      const currentTime = Date.now() / 1000; // Convert to epoch seconds
      let startTime;
      
      if (!lastFetchedTime) {
        // First time fetching older notifications (7-14 days ago)
        startTime = currentTime - 14 * 24 * 60 * 60; // 14 days ago
        setLastFetchedTime(startTime);
      } else {
        // Subsequent fetches (14-21, 21-28, etc.)
        startTime = lastFetchedTime - 7 * 24 * 60 * 60; // 7 days before last fetched time
        setLastFetchedTime(startTime);
      }

      let res = await server.getAllNotifications(client_id, service_id, email, 'home', undefined, startTime, currentTime);
      if(res.status === 200){
        const data = await res.json();
        if (data.notifications && data.notifications.length > 0) {
          // Filter out any duplicate notifications
          const newNotifications = data.notifications.filter(newNotif => 
            !notifications.some(existingNotif => existingNotif.id === newNotif.id)
          );
          
          // Append new notifications to existing ones
          setNotifications(prevNotifications => [...prevNotifications, ...newNotifications]);
          
          // Show success message if new notifications were added
          if (newNotifications.length > 0) {
            showToast(`Added ${newNotifications.length} older notifications`, "success");
          } else {
            showToast('No more older notifications available', "info");
          }
        } else {
          showToast('No more older notifications available', "info");
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showToast('Failed to fetch older notifications', "error");
    }
  }

  const resetOlderNotifications = () => {
    setLastFetchedTime(null);
    setShowOlderNotifications(false);
    // Reset to only show recent notifications
    getAllNotifications();
  }

  const getIconForType = (type) => {
    switch(type) {
      case 'alert':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'billing':
        return <CreditCard className="h-5 w-5 text-purple-500" />;
      case 'download':
        return <Download className="h-5 w-5 text-blue-500" />;
      case 'new-feature':
        return <Zap className="h-5 w-5 text-yellow-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getBgColorForType = (type) => {
    switch(type) {
      case 'alert':
        return 'bg-red-50';
      case 'billing':
        return 'bg-purple-50';
      case 'download':
        return 'bg-blue-50';
      case 'new-feature':
        return 'bg-yellow-50';
      default:
        return 'bg-gray-50';
    }
  };

  const getBorderColorForType = (type) => {
    switch(type) {
      case 'alert':
        return 'border-l-red-500';
      case 'billing':
        return 'border-l-purple-500';
      case 'download':
        return 'border-l-blue-500';
      case 'new-feature':
        return 'border-l-yellow-500';
      default:
        return 'border-l-gray-400';
    }
  };
  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const getUnreadNotificationCount = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      let timenow = parseInt(Date.now() / 1000);
      let timelast7Days = timenow - 7 * 24 * 60 * 60;
      
      const resp = await server.getUnreadNotificationCount(client_id, service_id, email, 'home', undefined, timelast7Days, timenow);
      if(resp.status === 200){
        const data = await resp.json();
        setUnreadCount(data.unread_notifications);
      }
    } catch (error) {
    } finally {
      isFetchingRef.current = false;
    }
  }
  useEffect(() => {
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if (user) {
      setEmail(user.email);
    }
    if(serviceID){
      setService_id(serviceID);
    }
    if(clientID){
      setClient_id(clientID)
    }
  }, [serviceID, clientID]);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only set up interval if we have all required data
    if (client_id && service_id && email) {
      
      // Initial call
      getUnreadNotificationCount();
      
      // Set up interval
      intervalRef.current = setInterval(() => {
        getUnreadNotificationCount();
      }, 15000);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isFetchingRef.current = false;
    };
  }, [client_id, service_id, email]);

  useEffect(() => {
    const user = getCookie('dashboard_login_user_creds')
      ? JSON.parse(getCookie('dashboard_login_user_creds'))
      : null;
  
    if (user?.email && user?.client_id) {
      scheduleAccessTokenRefresh(server);
    }
  }, []); // <-- important


  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button 
        onClick={toggleNotificationPanel}
        className="relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel - Slides in from right */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={toggleNotificationPanel}
          ></div>
          
          {/* Panel */}
          <div className="fixed inset-y-0 right-0 bg-white shadow-lg z-50 flex flex-col transition-transform duration-300 ease-in-out transform translate-x-0" style={{width: "30rem"}}>
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-800">Notifications</h3>
              <button 
                onClick={toggleNotificationPanel}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'unread' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('unread')}
              >
                Unread ({unreadCount})
              </button>
              <button
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'all' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('all')}
              >
                All
              </button>
            </div>

            {/* Actions Bar */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              {activeTab === 'unread' && unreadCount > 0 && (
                <button 
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <Check className="mr-1 h-3 w-3" />
                  Mark all as read
                </button>
              )}
              {activeTab === 'all' && (
                <span className="text-xs text-gray-500">
                  {showOlderNotifications ? 'Showing all notifications' : 'Recent (7 days)'}
                </span>
              )}
              <div className="flex-grow"></div>
            </div>

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto">
              {displayedNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center justify-center h-full">
                  <Bell className="h-10 w-10 text-gray-300 mb-2" />
                  <p>{activeTab === 'unread' ? 'No unread notifications' : 'No notifications'}</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {!loading && displayedNotifications.map((notification) => (
                    <li 
                      key={notification.id} 
                      className={`relative ${notification.is_read ? '' : 'bg-blue-50'} hover:bg-gray-50 transition-colors duration-150`}
                    >
                      <div className={`flex p-4 border-l-4 ${getBorderColorForType(notification.type)}`}>
                        <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full ${getBgColorForType(notification.type)}`}>
                          {getIconForType(notification.type)}
                        </div>
                        
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {notification.readable_time}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          
                          <div className="mt-2 flex items-center justify-between">
                            {notification.hasDetails && (
                              <a href="#" className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800">
                                View details
                                <ExternalLink className="ml-1 h-3 w-3" />
                              </a>
                            )}
                            
                            {!notification.is_read && (
                              <button 
                                onClick={() => markAsRead(notification.id)}
                                className="inline-flex items-center text-xs text-gray-600 hover:text-gray-800"
                              >
                                Mark as read
                                <Check className="ml-1 h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {
                    loading && (
                      <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                      </div>
                    )
                  }
                  
                  {/* Show older notifications toggle in All tab */}
                  {activeTab === 'all' && (
                    <li className="p-4 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <button 
                          onClick={() => handleShowOlderNotifications()}
                          className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
                        >
                          <Clock className="mr-2 h-4 w-4" />
                          {lastFetchedTime ? 'Load More Older Notifications' : 'Show Older Notifications'}
                        </button>
                        {lastFetchedTime && (
                          <button 
                            onClick={resetOlderNotifications}
                            className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center"
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Reset to Recent
                          </button>
                        )}
                      </div>
                    </li>
                  )}
                </ul>
              )}
            </div>
            

          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsPanel;